import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { StaticDatePicker, PickersDay } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import configServ from "../../services/config";
import CardHeadMenu from "../../Components/Card/CardHeadMenu/CardHeadMenu";

// Styled doctor card
const DoctorCard = styled(Paper)(({ theme, selected }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  cursor: "pointer",
  textAlign: "center",
  backgroundColor: selected
    ? theme.palette.primary.main
    : theme.palette.background.paper,
  color: selected ? "#fff" : "inherit",
  boxShadow: selected ? theme.shadows[6] : theme.shadows[1],
  transition: "all 0.3s ease",
}));

// Styled slot box
const SlotBox = styled(Box)(({ status }) => {
  let backgroundColor = "#4caf50"; // default: available

  if (status === "booked") backgroundColor = "#f44336"; // red
  else if (status === "overbooked") backgroundColor = "#ff9800"; // orange

  return {
    padding: "8px 12px",
    margin: "6px",
    borderRadius: 6,
    minWidth: 80,
    textAlign: "center",
    backgroundColor,
    color: "#fff",
    fontWeight: 500,
    fontSize: 14,
  };
});

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== "isAvailable" && prop !== "isPast" && prop !== "isAvailableInPast",
})(({ theme, ownerState }) => {
  const { isAvailable, isPast, isAvailableInPast } = ownerState;

  if (isPast) {
    if (isAvailableInPast) {
      return {
        backgroundColor: "#bbdefb", // light blue
        color: "#000",
        "&:hover": {
          backgroundColor: "#90caf9",
        },
      };
    } else {
      return {
        backgroundColor: "#f0f0f0",
        color: theme.palette.text.disabled,
        pointerEvents: "none",
      };
    }
  }

  if (isAvailable) {
    return {
      backgroundColor: "#c8e6c9",
      color: "#000",
      "&:hover": {
        backgroundColor: "#a5d6a7",
      },
    };
  }

  return {
    backgroundColor: "#ffcdd2",
    color: "#000",
    pointerEvents: "none",
    "&:hover": {
      backgroundColor: "#ef9a9a",
    },
  };
});

const BookingSlot = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [doctorAvailableDays, setDoctorAvailableDays] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingg, setloadingg] = useState(false);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const res = await configServ.getAllPractitioners();
        if (res.success) {
          setDoctors(res.data || []);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchDoctors();
  }, []);

  const fetchAppointmentDays = async (doctorId) => {
    try {
      const res = await configServ.getDoctorAvailableDays({
        doctor_id: doctorId,
      });

      if (res.success && res.data.length > 0) {
        setDoctorAvailableDays(res.data);
      } else {
        setDoctorAvailableDays([]);
        setSelectedDate(null); // ✅ Important: clear date when no available days
        setAvailableTimeSlots([]); // Optional: also clear slots
      }
    } catch (err) {
      console.error(err);
      setDoctorAvailableDays([]);
      setSelectedDate(null); // ✅ reset on error too
      setAvailableTimeSlots([]);
    }
  };

  const fetchAppointmentSlots = async (doctorId, pickDate) => {
    try {
      setloadingg(true);
      setAvailableTimeSlots([]);
      const res = await configServ.getDoctorSlotsAll({
        doctor_id: doctorId,
        date: pickDate,
      });
      if (res.success) {
        setAvailableTimeSlots(res.data.slots || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setloadingg(false);
    }
  };

  const handleDoctorSelect = async (doctor) => {
    setLoading(true);
    setSelectedDoctor(doctor);
    setSelectedDate(null); // ✅ Reset date here
    setAvailableTimeSlots([]);
    await fetchAppointmentDays(doctor.id);
    setLoading(false);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    const formattedDate = dayjs(date).format("YYYY-MM-DD");
    if (selectedDoctor?.id) {
      fetchAppointmentSlots(selectedDoctor.id, formattedDate);
    }
  };

  const isDayAvailable = (date) => {
    const dayName = dayjs(date).format("dddd");
    return doctorAvailableDays.includes(dayName);
  };

  // const renderCustomDay = (date, selectedDate, pickersDayProps) => {
  //   const isAvailable = isDayAvailable(date);
  //   const isPast = dayjs(date).isBefore(dayjs(), "day");
  //   return (
  //     <CustomPickersDay
  //       {...pickersDayProps}
  //       ownerState={{ ...pickersDayProps, isAvailable, isPast }}
  //     />
  //   );
  // };

  return (
    <Box p={isMobile ? 1 : 3} sx={{ width: "100%" }}>
      <CardHeadMenu
        prefix={"Na"}
        suffix={"Na"}
        title={"Booking Statu's"}
        // handlePopup={handleClickOpen}
        showCreate={false}
      />
      <Grid container spacing={isMobile ? 2 : 3} mt={2}>
        {/* Doctor Selection */}
        <Grid item xs={12} md={3}>
          <Paper elevation={3} style={{ padding: 16 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "blue",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              Select Doctor
            </Typography>

            {doctors.map((doctor) => (
              <DoctorCard
                key={doctor.id}
                selected={selectedDoctor?.id === doctor.id}
                onClick={() => handleDoctorSelect(doctor)}
              >
                <Typography variant="subtitle1">{doctor.fname}</Typography>
              </DoctorCard>
            ))}
          </Paper>
        </Grid>

        {/* Calendar */}
        <Grid item xs={12} md={5}>
          <Paper
            elevation={3}
            style={{ padding: 16, textAlign: "center", minHeight: 440 }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "blue",
                fontWeight: "bold",
                textAlign: "center",
              }}
              gutterBottom
            >
              Select Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {loading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="300px"
                >
                  <CircularProgress />
                </Box>
              ) : (
                <StaticDatePicker
                  displayStaticWrapperAs={isMobile ? "mobile" : "desktop"}
                  value={selectedDate}
                  onChange={handleDateChange}
                  renderDay={(day, selectedDates, pickersDayProps) => {
                    const isPast = day.isBefore(dayjs(), "day");
                    const dayName = day.format("dddd");
                    const isAvailable = doctorAvailableDays.includes(dayName);

                    return (
                      <CustomPickersDay
                        {...pickersDayProps}
                        day={day}
                        ownerState={{
                          ...pickersDayProps,
                          isPast,
                          isAvailable,
                          isAvailableInPast: isPast && isAvailable,
                        }}
                      />
                    );
                  }}
                />
              )}
            </LocalizationProvider>
          </Paper>
        </Grid>

        {/* Slot View */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} style={{ padding: 16, textAlign: "center" }}>
            <Typography
              variant="h6"
              sx={{
                color: "blue",
                fontWeight: "bold",
                textAlign: "center",
              }}
              gutterBottom
            >
              Time Slot Availability
            </Typography>

            {/* Legend */}
            <Box display="flex" justifyContent="center" mb={2} gap={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  width={16}
                  height={16}
                  borderRadius={4}
                  bgcolor="#4caf50"
                />
                <Typography variant="caption">Available</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  width={16}
                  height={16}
                  borderRadius={4}
                  bgcolor="#f44336"
                />
                <Typography variant="caption">Booked</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  width={16}
                  height={16}
                  borderRadius={4}
                  bgcolor="#ff9800"
                />
                <Typography variant="caption">Overbooked</Typography>
              </Box>
            </Box>

            {/* Slots */}
            {loading || loadingg ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="180px"
              >
                <CircularProgress size={28} />
              </Box>
            ) : availableTimeSlots.length > 0 ? (
              <Box display="flex" flexWrap="wrap" justifyContent="center">
                {availableTimeSlots.map((slot, idx) => (
                  <SlotBox key={idx} status={slot.status}>
                    {slot.time}
                  </SlotBox>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                {selectedDate
                  ? "No slots available"
                  : "Select a date to view slots"}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BookingSlot;
